import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Skeleton } from "@mui/material";

interface AboutProps {
  about?: string;
  isLoading: boolean;
}

const aboutTextStyle = {
  color: "var(--Dark-Mode---25, #5E5E5E)",
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
};

const About = ({ about, isLoading }: AboutProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "about" });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      const { clientHeight, scrollHeight } = textElement;
      if (clientHeight < scrollHeight) {
        setIsClamped(true);
      }
    }
  }, [about]);

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          background: "#FFF",
          padding: "20px",
        }}
      >
        <Typography
          sx={{
            color: "var(--black-100, #1C1C1C)",
            fontFamily: "Montserrat",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "24px",
            marginBottom: "10px",
          }}
        >
          {t("about")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="35%" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: "#FFF",
        padding: "20px",
      }}
    >
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "24px",
          marginBottom: "10px",
        }}
      >
        {t("about")}
      </Typography>

      {!about ? (
        <Typography sx={aboutTextStyle}>{t("noAbout")}</Typography>
      ) : (
        <>
          <Typography
            ref={textRef}
            sx={{
              ...aboutTextStyle,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              WebkitLineClamp: isExpanded ? "none" : 4,
            }}
          >
            {about}
          </Typography>
          {isClamped && (
            <Button
              onClick={toggleExpansion}
              variant="text-md"
              sx={{
                marginTop: "10px",
                padding: 0,
                "&:hover": {
                  background: "none",
                },
              }}
            >
              {isExpanded ? t("showLess") : t("showMore")}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default About;
